import type { Permission, UserFragment, UserRole } from '@gql'

export type MatchAccessType = 'all' | 'any'
export type UserAccessUnit = { permission: Permission } | { role: UserRole }
export type UserAccess = UserAccessUnit | { any: UserAccessUnit[] } | { all: UserAccessUnit[] }

export const hasPermission = (permission: Permission, user: UserFragment | null | undefined) =>
  user?.accountPermissions.map((perm) => perm.code).includes(permission)

export const hasAnyPermissions = (
  permissions: readonly Permission[],
  user: UserFragment | null | undefined,
) => permissions?.some((permission) => hasPermission(permission, user)) || false

export const hasAllPermissions = (
  permissions: readonly Permission[],
  user: UserFragment | null | undefined,
) => permissions?.every((permission) => hasPermission(permission, user)) || false

export function hasPermissions(
  permissions: readonly Permission[],
  user: UserFragment | null | undefined,
  matchAccess: MatchAccessType = 'all',
) {
  if (matchAccess === 'all') {
    return hasAllPermissions(permissions, user)
  }

  return hasAnyPermissions(permissions, user)
}

export function canUserAccessUnit(
  user: UserFragment | null | undefined,
  accessUnit: UserAccessUnit,
): boolean {
  if ('permission' in accessUnit) {
    const userPermissions = (user?.accountPermissions || []).map((permission) => permission.code)
    return userPermissions.includes(accessUnit.permission)
  } else {
    const userRoles = user?.roles || []
    return userRoles.includes(accessUnit.role)
  }
}

export function hasUserAccess(
  user: UserFragment | null | undefined,
  userAccess: UserAccess,
): boolean {
  if ('any' in userAccess) {
    return userAccess.any.some((unit) => canUserAccessUnit(user, unit))
  } else if ('all' in userAccess) {
    return userAccess.all.every((unit) => canUserAccessUnit(user, unit))
  } else {
    return canUserAccessUnit(user, userAccess)
  }
}
